import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Bell, PenLine, X, ChevronDown, FileText, AlertTriangle, Clock, Calendar } from 'lucide-react';

export default function PendingActionsPanel({
  pendingActions,
  pendingRequirements = [],
  showPendingActions,
  setShowPendingActions,
  user,
  setSelectedDocForWorkflow,
  setSignatureModalOpen,
  onUploadForRequirement
}) {
  const hasSignatureActions = pendingActions.length > 0;
  const hasRequirements = pendingRequirements.length > 0;
  const overdueCount = pendingRequirements.filter(r => r.status === 'overdue').length;
  const totalPending = pendingActions.length + pendingRequirements.length;
  
  if (totalPending === 0) return null;

  // Expanded panel
  if (showPendingActions) {
    return (
      <div className="mx-6 mt-4 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            <h3 className="font-semibold text-amber-800 dark:text-amber-200">
              Pending Actions ({totalPending})
              {overdueCount > 0 && (
                <Badge className="ml-2 bg-red-500 text-white text-[10px]">{overdueCount} Overdue</Badge>
              )}
            </h3>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowPendingActions(false)}
            className="h-6 w-6 p-0 text-amber-600 dark:text-amber-400 hover:text-amber-800"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
        
        <div className="space-y-3 max-h-64 overflow-y-auto">
          {/* Overdue Document Requirements - Show First */}
          {pendingRequirements.filter(r => r.status === 'overdue').map((req) => (
            <div
              key={req.id}
              className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-900/30 rounded-lg border border-red-300 dark:border-red-700"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900/50 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <p className="font-medium text-red-800 dark:text-red-200 text-sm">{req.document_type}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge className="text-[10px] bg-red-500 text-white">OVERDUE</Badge>
                    <span className="text-xs text-red-600 dark:text-red-400">
                      Due: {new Date(req.due_date).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{req.description}</p>
                </div>
              </div>
              <Button 
                size="sm" 
                className="bg-red-500 hover:bg-red-600 text-white rounded-full"
                onClick={() => onUploadForRequirement && onUploadForRequirement(req)}
              >
                Upload Now
              </Button>
            </div>
          ))}
          
          {/* Signature Actions */}
          {pendingActions.map((doc) => {
            const userAssignment = doc.assignees_with_roles?.find(
              a => a.email?.toLowerCase() === user?.email?.toLowerCase()
            );
            return (
              <div
                key={doc.id}
                className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg border border-amber-200 dark:border-amber-700 hover:border-amber-400 cursor-pointer transition-colors"
                onClick={() => {
                  setSelectedDocForWorkflow(doc);
                  setSignatureModalOpen(true);
                }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center">
                    <PenLine className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-800 dark:text-slate-200 text-sm">{doc.name}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge className={`text-[10px] uppercase ${
                        userAssignment?.role === 'reviewer' ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300' :
                        userAssignment?.role === 'approver' ? 'bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300' :
                        'bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300'
                      }`}>
                        {userAssignment?.role || 'reviewer'}
                      </Badge>
                      <span className="text-xs text-slate-500 dark:text-slate-400">Signature Required</span>
                    </div>
                  </div>
                </div>
                <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white rounded-full">
                  Sign Now
                </Button>
              </div>
            );
          })}
          
          {/* Pending Document Requirements (not overdue) */}
          {pendingRequirements.filter(r => r.status === 'pending').slice(0, 3).map((req) => (
            <div
              key={req.id}
              className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="font-medium text-blue-800 dark:text-blue-200 text-sm">{req.document_type}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge className="text-[10px] bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300">
                      {req.phase_label}
                    </Badge>
                    <span className="text-xs text-blue-600 dark:text-blue-400 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Due: {new Date(req.due_date).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>
              <Button 
                size="sm" 
                variant="outline"
                className="border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/50 rounded-full"
                onClick={() => onUploadForRequirement && onUploadForRequirement(req)}
              >
                Upload
              </Button>
            </div>
          ))}
          
          {/* Show more link if there are more pending requirements */}
          {pendingRequirements.filter(r => r.status === 'pending').length > 3 && (
            <p className="text-xs text-center text-slate-500 dark:text-slate-400 pt-2">
              +{pendingRequirements.filter(r => r.status === 'pending').length - 3} more pending documents
            </p>
          )}
        </div>
      </div>
    );
  }

  // Collapsed badge
  return (
    <div className="mx-6 mt-4">
      <Button
        variant="outline"
        size="sm"
        onClick={() => setShowPendingActions(true)}
        className={`${overdueCount > 0 ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 hover:bg-red-100' : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700 text-amber-700 dark:text-amber-300 hover:bg-amber-100'}`}
      >
        {overdueCount > 0 ? <AlertTriangle className="w-4 h-4 mr-2" /> : <Bell className="w-4 h-4 mr-2" />}
        {totalPending} Pending Action{totalPending > 1 ? 's' : ''}
        {overdueCount > 0 && <Badge className="ml-2 bg-red-500 text-white text-[10px]">{overdueCount} Overdue</Badge>}
        <ChevronDown className="w-4 h-4 ml-2" />
      </Button>
    </div>
  );
}
